/**
 * AdminDashboardOverview Component
 *
 * Admin version of dashboard overview showing global audit metrics
 */

import React from 'react';
import {
  Assessment as NotesIcon,
  Business as OrgsIcon,
  CheckCircle as CPTIcon,
  Paid as RevenueIcon,
  Repeat as RevisionsIcon,
  TrendingUp as QualityIcon,
  Verified as ComplianceIcon,
} from '@mui/icons-material';
import { Alert, AlertTitle, Box, Button, Chip, Grid } from '@mui/material';

import { useAdminDashboardStats } from '../../hooks/admin';
import { FlagSummary } from '../dashboard/FlagSummary';
import { QualityDistribution } from '../dashboard/QualityDistribution';
import { StatsCard } from '../dashboard/StatsCard';

/**
 * Format cents to dollar string
 */
const formatRevenue = (cents: number | null | undefined): string => {
  if (cents === null || cents === undefined) return '—';
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(dollars);
};

/**
 * Format percentage
 */
const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(0)}%`;
};

export const AdminDashboardOverview: React.FC = () => {
  const { stats, qualityDistribution, isLoading, error, refetch } = useAdminDashboardStats();

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Error loading dashboard</AlertTitle>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Global indicator */}
      <Box sx={{ mb: 2 }}>
        <Chip
          icon={<OrgsIcon sx={{ fontSize: 16 }} />}
          label={`${stats?.total_organizations || 0} Organizations`}
          color="primary"
          variant="outlined"
          size="small"
        />
      </Box>

      {/* Stats Cards Grid */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Notes Audited */}
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            icon={<NotesIcon />}
            label="Notes Audited"
            value={stats?.total_notes_audited}
            subtitle={`${stats?.period_days || 30} day period`}
            isLoading={isLoading}
            color="primary"
          />
        </Grid>

        {/* Average Quality Score */}
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            icon={<QualityIcon />}
            label="Avg Quality Score"
            value={stats?.avg_quality_score?.toFixed(1)}
            suffix="/ 5"
            subtitle={
              stats?.avg_quality_improvement
                ? `${stats.avg_quality_improvement > 0 ? '+' : ''}${stats.avg_quality_improvement.toFixed(1)} improvement`
                : undefined
            }
            isLoading={isLoading}
            color="success"
          />
        </Grid>

        {/* Compliance Rate */}
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            icon={<ComplianceIcon />}
            label="Compliance Rate"
            value={formatPercent(stats?.avg_compliance_pct)}
            isLoading={isLoading}
            color="info"
          />
        </Grid>

        {/* CPT Match Rate */}
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            icon={<CPTIcon />}
            label="CPT Match Rate"
            value={formatPercent(stats?.cpt_match_rate)}
            subtitle="AI agrees with provider"
            isLoading={isLoading}
            color="success"
          />
        </Grid>

        {/* Revenue Delta */}
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            icon={<RevenueIcon />}
            label="Revenue Opportunity"
            value={formatRevenue(stats?.total_revenue_delta_cents)}
            subtitle="Potential missed revenue"
            isLoading={isLoading}
            color="warning"
          />
        </Grid>

        {/* Total Revisions */}
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            icon={<RevisionsIcon />}
            label="Total Revisions"
            value={stats?.total_revisions}
            subtitle="Re-audits performed"
            isLoading={isLoading}
            color="primary"
          />
        </Grid>
      </Grid>

      {/* Quality Distribution and Flag Summary */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <QualityDistribution distribution={qualityDistribution} isLoading={isLoading} />
        </Grid>
        <Grid item xs={12} md={6}>
          <FlagSummary
            criticalFlags={stats?.total_critical_flags}
            highFlags={stats?.total_high_flags}
            mediumFlags={stats?.total_medium_flags}
            lowFlags={stats?.total_low_flags}
            notesWithCriticalFlags={stats?.notes_with_critical_flags}
            notesClean={stats?.notes_clean}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboardOverview;

